<template>
  <div class="downloadbutton-container">
    <Modal
      title="Edit File"
      :show="showModal.edit"
      @close="showModal.edit = false">
      <FileEdit
        :file="file"
        :topicKey="topicKey"
        :successCallback="editSuccessCallback"
      />
    </Modal>
    <div
      @click="showEditModal"
      class="download-button"
      :class="[getFileTypeClass, {'edit-mode': hasEditPermissions}]"
      @click.prevent="download"
    >
      <div class="title-container">
        <div class="label"><slot /></div>
      </div>
      <div class="action-container">
        <span
          v-if="file.metadata.numPages"
          :class="[
            getNumPages() > 1 ? 'pages-icon' : 'page-icon',
            {'black-icon': getFileType() === 'PPT'}
          ]"
        >
          <b :class="{'double-digits': file.metadata.numPages >= 10}">
            {{ file.metadata.numPages }}
          </b>
        </span>
        <span class="file-date">{{ getDateString() }}</span>
      </div>
    </div>
    <div v-if="hasReplacePermissions" @click="showModal.replace = true" class="replace-icon"></div>
    <Modal
      title="Replace File"
      :show="showModal.replace"
      @close="showModal.replace = false">
      <FileUploaderMultiple
        :files="[
          { ...file, accepts: accepts, required: true },
        ]"
        :topicKey="topicKey"
        :securityLevel="securityLevel"
        :isReplace=true
        :unionHash="file.metadata.unionHash"
        :successCallback="replaceFile"
      >
      </FileUploaderMultiple>
    </Modal>
    <div v-if="hasDeletePermissions" @click="showModal.delete = true" class="delete-icon"></div>
    <Modal
      title="Delete File"
      :show="showModal.delete"
      @close="showModal.delete = false">
      <div class="delete-dialog">
        <span class="url"><b>url:</b> ../{{ getURL(file) }}</span>
        <span class="display-name"><b>Display name:</b> {{ file.alias }}</span>
        <p>Are you sure you want to delete this file?</p>
        <button @click="deleteFile" class="btn">Delete</button>
      </div>
    </Modal>
  </div>
</template>

<script>

import projectconfig from '@/project-config';

import Modal from '@/components/modal.vue';
import FileEdit from '@/components/file-edit.vue';
import FileUploaderMultiple from '@/components/file-uploader-multiple.vue';

import deleteObject from '@/handlers/deleteObject';

export default {
  name: 'DownloadButtonBlob',
  components: {
    Modal,
    FileEdit,
    FileUploaderMultiple,
  },
  props: {
    file: {
      type: Object,
      required: true,
    },
    topicKey: {
      type: String,
      required: true,
    },
    securityLevel: {
      type: String,
      default: 'information',
    },
    accepts: {
      type: String,
      default: '.pdf,.pptx',
    },
    hasEditPermissions: {
      type: Boolean,
      default: false,
    },
    hasReplacePermissions: {
      type: Boolean,
      default: false,
    },
    hasDeletePermissions: {
      type: Boolean,
      default: false,
    },
    deleteCallback: {
      type: Function,
      default: () => {},
    },
    replaceCallback: {
      type: Function,
      default: () => {},
    },
    editCallback: {
      type: Function,
      default: () => {},
    },
  },
  data: () => ({
    showModal: {
      edit: false,
      upload: false,
      replace: false,
      delete: false,
    },
  }),
  computed: {
    getFileTypeClass() {
      switch (this.getFileType()) {
        case 'PDF':
          return 'file-type-pdf';
        case 'PPT':
          return 'file-type-ppt';
        default:
          return 'file-type-pdf';
      }
    },
  },
  methods: {
    showEditModal() {
      if (this.hasEditPermissions) {
        this.showModal.edit = true;
      }
    },
    async download() {
      // hasEditPermission is only true when edit mode is active
      // ..and when edit mode is active the entire button is clickable to open the edit modal
      if (!this.hasEditPermissions) {
        window.open(this.file.dynamicURL, '_blank');
      }
    },
    async deleteFile() {
      await deleteObject(this.file.key, this.deleteCallback);
      this.showModal.delete = false;
    },
    replaceFile(topicKey) {
      this.showModal.replace = false;
      this.replaceCallback(topicKey);
    },
    getFileType() {
      const { type } = this.file.metadata;
      if (type === 'application/pdf') {
        return 'PDF';
      }
      if (type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
        return 'PPT';
      }
      if (type === 'application/zip') {
        return 'ZIP';
      }
      return 'Unknown';
    },
    getNumPages() {
      const { numPages } = this.file.metadata;
      if (numPages) {
        return numPages;
      }
      return 0;
    },
    getDateString() {
      const { lastModified } = this.file.metadata;
      const date = new Date(lastModified);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
      }).toUpperCase();
    },
    getURL(file) {
      if (projectconfig.URL_FORMATTING.MODE === 'RELATIVE') {
        return file.dynamicURL;
      }
      return file.Key;
    },
    editSuccessCallback() {
      this.showModal.edit = false;
      this.editCallback();
    },
  },
};
</script>

  <style lang="stylus" scoped>
    @require '../style/colors'

    .downloadbutton-container
      display: flex
      align-items: center
      justify-content: flex-start
      flex-wrap: nowrap
      column-gap: 15px

    .download-button
      display: flex
      align-items: center
      align-content: flex-start
      justify-content: space-between
      flex-grow: 100
      padding: 14px 24px
      font-size: 24px
      font-weight: 700
      color: $primary-color
      border: 3px solid $primary-color
      border-radius: 8px
      column-gap: 20px;
      width: 100%
      cursor pointer

      .title-container
        display: flex
        align-items: center
        align-content: flex-start
        column-gap: 20px;

      .action-container
        display: flex
        align-items: center
        align-content: flex-start
        column-gap: 20px;

      img
        width: 40px
        order: 3
        cursor: pointer

      &:hover
        outline: 2px solid $primary-color
        outline-offset: 0px
      &.file-type-ppt:hover
        outline: 2px solid $black

    .edit-mode
      border-style: dashed
      cursor pointer

      &:hover
        outline: 1px dotted $primary-color
        outline-offset: 2px

    .delete-icon
    .replace-icon
    .pages-icon
    .pages-icon
    .download-icon
    .edit-icon
      width: 30px
      height: 30px
      background-size: contain
      cursor: pointer
    .delete-icon
      background: url('../assets/delete_document.svg') no-repeat
    .replace-icon
      background: url('../assets/update_content_document.svg') no-repeat
    .page-icon
    .pages-icon
      background: url('../assets/Pages.svg') no-repeat
      width: 33px
      height: 33px
      &.black-icon
        background: url('../assets/Pages-black.svg') no-repeat
      b
        position: relative
        top: -.3rem
        right: -.3rem
        font-size: 1.6rem
      .double-digits
        right: .3rem
    .page-icon
      background: url('../assets/Page.svg') no-repeat
      &.black-icon
        background: url('../assets/Page-black.svg') no-repeat
      b
        right: -.6rem

    .download-icon
      background: url('../assets/Download.svg') no-repeat
    .edit-icon
      background: url('../assets/edit_name_document.svg') no-repeat
      width: 25px
      height: 25px

    .file-type
      font-size: 1.6rem
    .file-type-pdf
      color: $primary-color
    .file-type-ppt
      color: $black
      border-color: $black

    .file-date
      margin-right: 3rem
      font-size: 2rem
      font-weight: 100

    .delete-dialog
      display: flex
      flex-direction: column
      align-items: center
      gap: 1rem

      .url
      .display-name
        font-size: 15px
        padding: 2rem
        min-width: 65%

      .url
        color: $black80
        background-color: #DEDEDE

      .display-name
        color: $blue
        background-color: $blue30
        color: $primary-color
        background-color: $secondary-color

      p
        font-size: 1.8rem
        margin-bottom: 2rem
        font-family: space-grotesk

      button
        background-color: $primary-color
        color: $white
        font-size: 2rem
        padding: 1.5rem 3rem
        border: none
        border-radius: .5rem
        cursor: pointer
        transition: background-color 0.3s ease-in-out

        &:hover
          background-color: darken($primary-color, 10%)

  </style>
